import React, { useMemo } from "react"
import { Button, Select, Tag } from "antd"
import { compileBanner } from "../../compiler/banner"
import { shownNames } from "../../model/shownLabels"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import Field from "./Field.component"
import { useInspectedTable } from "./useInspectedTable"

/** The choice that removes the table's own banner, so the spec's default applies. */
const DEFAULT_CHOICE = "__default__"

/**
 * The banner of the table: the spec's default or one of its banners, and
 * the columns that banner gives, read only. The banners themselves are
 * edited in the banner editor, which `onEditBanners` opens.
 */
const ColumnsTab: React.FC<{ tableId: string; onEditBanners?: () => void }> = ({
  tableId,
  onEditBanners,
}) => {
  const inspected = useInspectedTable(tableId)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)

  const banner = inspected?.resolved.banner ?? null
  const catalogue = inspected?.catalogue ?? null
  const labels = inspected?.document.labels
  // The columns as the table will show them, one heading per group, each
  // name as shown. Grouped by the engine's heading, which is unique.
  const groups = useMemo(() => {
    if (!banner || !catalogue) return []
    const out: { group: string; heading: string; labels: string[] }[] = []
    compileBanner(banner, catalogue, shownNames(labels)).columns.forEach((c) => {
      const last = out[out.length - 1]
      if (last && last.group === c.group) last.labels.push(c.display)
      else out.push({ group: c.group, heading: c.groupDisplay, labels: [c.display] })
    })
    return out
  }, [banner, catalogue, labels])

  if (!inspected) return null
  const { table, document } = inspected
  const fallback = document.banners.find((b) => b.id === document.defaults.bannerId)
  const options = [
    { value: DEFAULT_CHOICE, label: `Default (${fallback?.name ?? "none"})` },
    ...document.banners.map((b) => ({ value: b.id, label: b.name })),
  ]

  return (
    <div className="ts-tab">
      <Field label="Banner">
        <Select
          size="small"
          aria-label="Banner"
          className="ts-field__select"
          value={table.bannerId ?? DEFAULT_CHOICE}
          options={options}
          onChange={(value: string) =>
            value === DEFAULT_CHOICE
              ? clearOverrides(tableId, ["bannerId"])
              : updateTable(tableId, { bannerId: value })
          }
        />
      </Field>
      <Field label="Columns">
        {!banner ? (
          <p className="ts-hint">
            The banner this table used was deleted. Choose another banner.
          </p>
        ) : (
          <div className="ts-columns">
            <div className="ts-columns__group">
              <span className="ts-columns__heading">Total</span>
            </div>
            {groups.length === 0 && <p className="ts-hint">Total only.</p>}
            {groups.map((g, i) => (
              <div key={i} className="ts-columns__group">
                <span className="ts-columns__heading">{g.heading}</span>
                <span className="ts-columns__labels">
                  {g.labels.map((l, j) => (
                    <Tag key={j} className="ts-tag">
                      {l}
                    </Tag>
                  ))}
                </span>
              </div>
            ))}
          </div>
        )}
        {onEditBanners && (
          <div className="ts-actions">
            {/* It leads to another panel: a link, as the dashboards' way to the console. */}
            <Button type="link" size="small" className="ts-link" onClick={onEditBanners}>
              Edit banners
            </Button>
          </div>
        )}
      </Field>
    </div>
  )
}

export default ColumnsTab
